import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { AuthShell } from "@/components/auth-shell";
import { SocialAuthButtons } from "@/components/social-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { azError } from "@/lib/az-errors";
import { identifierToEmail, looksLikePhone, usernameToEmail, whatsappSupportLink, WHATSAPP_SUPPORT_LABEL } from "@/lib/username";

export const Route = createFileRoute("/giris")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Giriş — Albazar.az" },
      { name: "description", content: "Albazar.az hesabınıza daxil olun və elanlarınızı idarə edin." },
      { property: "og:title", content: "Giriş — Albazar.az" },
      { property: "og:description", content: "Hesabınıza daxil olun." },
    
      { property: "og:url", content: "https://albazar.az/giris" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/giris" }]
  }),
  component: SignIn,
});

function SignIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (user) navigate({ to: "/profil", replace: true });
  }, [user, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    let { error } = await supabase.auth.signInWithPassword({
      email: identifierToEmail(username),
      password,
    });
    // Köhnə hesablar üçün ehtiyat variant: istifadəçi adı kimi də yoxla
    if (error && looksLikePhone(username)) {
      const retry = await supabase.auth.signInWithPassword({
        email: usernameToEmail(username),
        password,
      });
      error = retry.error;
    }
    setBusy(false);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success("Xoş gəldiniz!");
    navigate({ to: "/profil", replace: true });
  };

  return (
    <>
    <AuthShell
      title="Hesaba giriş"
      subtitle="Google və ya telefon nömrəsi ilə 1 kliklə daxil olun"
      footer={
        <>
          Hesabınız yoxdur?{" "}
          <Link to="/qeydiyyat" className="text-gradient-auth font-bold">Qeydiyyatdan keçin</Link>
        </>
      }
    >
      <SocialAuthButtons />

      <form id="auth-form" className="space-y-4" onSubmit={submit}>
        <div>
          <Label htmlFor="username">Telefon nömrəsi və ya istifadəçi adı</Label>
          <Input
            id="username"
            inputMode="tel"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-2 h-11 rounded-2xl"
            placeholder="050 123 45 67"
          />
        </div>
        <div>
          <Label htmlFor="pw">Şifrə</Label>
          <Input
            id="pw"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 h-11 rounded-2xl"
            placeholder="••••••••"
          />
        </div>

        <Button
          type="submit"
          disabled={!hydrated || busy}
          className="gradient-auth h-12 w-full rounded-2xl border-0 text-base font-bold text-white shadow-elevated transition-opacity hover:opacity-90"
        >
          {busy ? "Gözləyin..." : "Daxil ol"}
        </Button>

        <Link
          to="/sifre-unutdum"
          className="text-gradient-auth flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border bg-accent/40 text-base font-semibold transition-colors hover:bg-accent"
        >
          <MessageCircle className="size-5 text-info" />
          Şifrəni unutmusunuz?
        </Link>
        <p className="text-center text-xs text-muted-foreground">
          WhatsApp dəstək: {WHATSAPP_SUPPORT_LABEL}
        </p>
      </form>
    </AuthShell>
      {hydrated && username.trim() && (
        <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-[45] px-4 md:hidden">
          <Button type="submit" form="auth-form" disabled={busy} className="gradient-auth border-0 font-bold text-white h-12 w-full rounded-2xl text-base shadow-elevated">
            {busy ? "Gözləyin..." : "Daxil ol"}
          </Button>
        </div>
      )}
    </>
  );
}
